import {
  BadgeCheck,
  Clock,
  AlertTriangle,
  Send,
  MessageSquare,
  CalendarDays,
  Hourglass,
  ChevronLeft,
  ChevronRight,
  Eye,
  Users,
  Mail,
  X,
  Info,
  CheckCircle,
  XCircle,
  Timer,
  Globe,
  Copy,
  ExternalLink,
  Hash,
} from "lucide-react";

import { useEffect, useRef, useState } from "react";

export const EmailScheduleList = ({
  schedule,
  hasMore,
  totalCount,
  onLoadMore,
}) => {
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [historyStack, setHistoryStack] = useState([]);
  const [loading, setLoading] = useState(false);
  const modalRef = useRef();

  const pageSize = 10;

  const currentPage = historyStack.length;
  const start = currentPage * pageSize + 1;
  const end = start + schedule.length - 1;

  const handleViewDetails = (batch) => {
    setSelectedBatch(batch);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedBatch(null);
  };

  const parseResponse = (response) => {
    const trackIdMatch = response.match(/Trackid=(\d+)/);
    const emailsMatch = response.match(/email_\d+_[^,\s]+/g);
    const batchEmailsMatch = response.match(/Batch to \[([^\]]+)\]/);

    return {
      trackId: trackIdMatch ? trackIdMatch[1] : null,
      emails: emailsMatch || [],
      batchEmails: batchEmailsMatch ? batchEmailsMatch[1].split(",") : [],
      emailCount: emailsMatch ? emailsMatch.length : 0,
    };
  };

  const handleNext = async () => {
    if (!schedule.length) return;
    const lastId = schedule[schedule.length - 1]._id;

    setLoading(true);
    setHistoryStack((prev) => [...prev, schedule[0]._id]);
    await onLoadMore("next", lastId);
    setLoading(false);
  };

  const handlePrev = async () => {
    if (!historyStack.length) return;
    const prevCursorId = historyStack[historyStack.length - 1];

    setLoading(true);
    await onLoadMore("prev", prevCursorId);
    setHistoryStack((prev) => prev.slice(0, -1));
    setLoading(false);
  };

  const statusIcon = (status) => {
    switch (status) {
      case "sent":
        return <BadgeCheck className="text-emerald-600" size={16} />;
      case "pending":
        return <Hourglass className="text-amber-500" size={16} />;
      case "failed":
        return <AlertTriangle className="text-red-500" size={16} />;
      default:
        return null;
    }
  };

  const statusColor = (status) => {
    switch (status) {
      case "sent":
        return "text-emerald-700 bg-emerald-50 border-emerald-200";
      case "pending":
        return "text-amber-700 bg-amber-50 border-amber-200";
      case "failed":
        return "text-red-700 bg-red-50 border-red-200";
      default:
        return "text-gray-700 bg-gray-50 border-gray-200";
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  // Extract subject from message
  const getSubject = (message) => {
    if (!message) return "No subject";
    const subjectMatch = message.match(/<h2[^>]*>([^<]*)<\/h2>/);
    return subjectMatch
      ? subjectMatch[1].replace(/<span>|<\/span>/g, "").trim()
      : "No subject";
  };

  // Get status based on responses
  const getStatus = (batch) => {
    if (!batch.responses || batch.responses.length === 0) return "pending";
    return "sent"; // Assuming if there are responses, it's sent
  };

  // Count total recipients
  const getTotalRecipients = (batch) => {
    return batch.recipients ? batch.recipients.length : 0;
  };

  // Count successful emails from responses
  const getSuccessCount = (batch) => {
    if (!batch.responses) return 0;
    return batch.responses.reduce((total, response) => {
      const parsed = parseResponse(response);
      return total + parsed.emailCount;
    }, 0);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        handleCloseModal();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [handleCloseModal]);

  return (
    <>
      <div
        className="flex flex-col border border-[#c7d2f5] rounded-2xl bg-white overflow-hidden"
        style={{ boxShadow: "0px 4px 24px rgba(67, 98, 193, 0.12)" }}
      >
        {schedule.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full py-16 px-4">
            <div className="p-4 bg-[#e7ebfb] rounded-full mb-4">
              <MessageSquare className="text-[#b3c0f3]" size={32} />
            </div>
            <p className="text-[#4F22D8] text-base font-semibold">
              No email batches found
            </p>
            <p className="text-[#8a99db] text-sm mt-1">
              Your email batches will appear here
            </p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm text-[#696969]">
                <thead className="sticky top-0 z-10 bg-[#4F22D8] shadow-sm">
                  <tr className="text-white">
                    <th className="px-6 py-4 text-left font-semibold tracking-wide">
                      <div className="flex items-center gap-2">
                        <Hash size={16} className="text-white/70" />
                        Batch ID
                      </div>
                    </th>

                    <th className="px-6 py-4 text-left font-semibold tracking-wide">
                      <div className="flex items-center gap-2">
                        <Users size={16} className="text-white/70" />
                        Recipients
                      </div>
                    </th>
                    <th className="px-6 py-4 text-left font-semibold tracking-wide">
                      <div className="flex items-center gap-2">
                        <Clock size={16} className="text-white/70" />
                        Status
                      </div>
                    </th>
                    <th className="px-6 py-4 text-left font-semibold tracking-wide">
                      <div className="flex items-center gap-2">
                        <CalendarDays size={16} className="text-white/70" />
                        Created At
                      </div>
                    </th>
                    <th className="px-6 py-4 text-left font-semibold tracking-wide">
                      <div className="flex items-center gap-2">
                        <Eye size={16} className="text-white/70" />
                        Actions
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {schedule.map((batch) => (
                    <tr
                      key={batch._id}
                      className="group hover:bg-[#f0f3fd] transition-colors duration-200 ease-in-out"
                    >
                      <td className="px-6 py-4">
                        <div className="font-mono text-sm font-semibold text-[#2e428b] bg-[#e7ebfb] px-3 py-1.5 rounded-lg inline-block">
                          {batch._id.slice(-6)}
                        </div>
                      </td>

                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <Users size={16} className="text-gray-400" />
                          <span className="font-medium">
                            {getTotalRecipients(batch)}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div
                          className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-2xl border font-medium text-sm ${statusColor(
                            getStatus(batch)
                          )}`}
                        >
                          {statusIcon(getStatus(batch))}
                          <span className="capitalize">{getStatus(batch)}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-gray-600 text-sm">
                          {formatDate(batch.createdAt)}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <button
                          onClick={() => handleViewDetails(batch)}
                          className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-[#4F22D8] bg-[#f0f3fd] rounded-lg hover:bg-[#e7ebfb] transition-colors duration-200"
                        >
                          <Eye size={16} />
                          View Details
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination Footer */}
            <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 bg-white">
              <div className="text-sm text-gray-500">
                Showing <span className="font-medium">{start}</span>–
                <span className="font-medium">{end}</span> of{" "}
                <span className="font-medium">{totalCount}</span> messages
              </div>
              <div className="flex items-center gap-4">
                <button
                  onClick={handlePrev}
                  disabled={historyStack.length === 0 || loading}
                  className={`flex items-center gap-1 px-3 py-1 rounded-md ${
                    historyStack.length === 0 || loading
                      ? "text-gray-400 cursor-not-allowed"
                      : "text-gray-700 hover:bg-gray-100"
                  }`}
                >
                  <ChevronLeft size={18} />
                  Previous
                </button>

                <button
                  onClick={handleNext}
                  disabled={!hasMore || loading}
                  className={`flex items-center gap-1 px-3 py-1 rounded-md ${
                    !hasMore || loading
                      ? "text-gray-400 cursor-not-allowed"
                      : "text-gray-700 hover:bg-gray-100"
                  }`}
                >
                  Next
                  <ChevronRight size={18} />
                </button>
              </div>
            </div>
          </>
        )}
      </div>
      {/* Modal */}
      {isModalOpen && selectedBatch && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2">
          <div
            ref={modalRef}
            className="bg-white rounded-xl shadow-xl w-full max-w-3xl max-h-[85vh]"
          >
            <div className="bg-[#4F22D8]  rounded-t-xl  text-white p-4 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="p-1 bg-white/20 rounded-md">
                  <MessageSquare size={18} />
                </div>
                <div>
                  <h2 className="text-base font-bold text-white">
                    {selectedBatch._id.slice(-6)}
                  </h2>
                  <p className="text-white/80 text-xs">
                    {getSubject(selectedBatch.message)}
                  </p>
                </div>
              </div>
              <button
                onClick={handleCloseModal}
                className="p-1 hover:bg-white/20 rounded-md transition-colors duration-200"
              >
                <X size={18} />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-4 overflow-y-auto max-h-[calc(85vh-156px)]">
              {/* Stats Cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4">
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-2">
                  <div className="flex items-center gap-2">
                    <div className="p-1 bg-emerald-100 rounded-md">
                      <CheckCircle className="text-emerald-600" size={16} />
                    </div>
                    <div>
                      <p className="text-emerald-600 font-semibold text-sm">
                        {getSuccessCount(selectedBatch)}
                      </p>
                      <p className="text-emerald-700 text-xs">Success</p>
                    </div>
                  </div>
                </div>

                <div className="bg-red-50 border border-red-200 rounded-lg p-2">
                  <div className="flex items-center gap-2">
                    <div className="p-1 bg-red-100 rounded-md">
                      <XCircle className="text-red" size={16} />
                    </div>
                    <div>
                      <p className="text-red font-semibold text-sm">
                        {getTotalRecipients(selectedBatch) -
                          getSuccessCount(selectedBatch)}
                      </p>
                      <p className="text-red text-xs">Failed</p>
                    </div>
                  </div>
                </div>

                <div className="bg-amber-50 border border-amber-200 rounded-lg p-2">
                  <div className="flex items-center gap-2">
                    <div className="p-1 bg-amber-100 rounded-md">
                      <Timer className="text-amber-600" size={16} />
                    </div>
                    <div>
                      <p className="text-amber-600 font-semibold text-sm">0</p>
                      <p className="text-amber-700 text-xs">Pending</p>
                    </div>
                  </div>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-2">
                  <div className="flex items-center gap-2">
                    <div className="p-1 bg-blue-100 rounded-md">
                      <Users className="text-blue-600" size={16} />
                    </div>
                    <div>
                      <p className="text-blue-600 font-semibold text-sm">
                        {getTotalRecipients(selectedBatch)}
                      </p>
                      <p className="text-blue-700 text-xs">Total</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Batch Details */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
                <div className="bg-gray-100 rounded-lg p-3">
                  <h3 className="font-semibold text-gray-900 text-sm mb-2 flex items-center gap-1">
                    <Info size={14} />
                    Batch Information
                  </h3>
                  <div className="space-y-1 text-sm">
                    <div className="flex justify-between">
                      <span className="text-grey">Batch ID:</span>
                      <span className="font-medium">
                        {selectedBatch._id.slice(-6)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-grey">Status:</span>
                      <div
                        className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-xs ${statusColor(
                          getStatus(selectedBatch)
                        )}`}
                      >
                        {statusIcon(getStatus(selectedBatch))}
                        <span className="capitalize">
                          {getStatus(selectedBatch)}
                        </span>
                      </div>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-grey text-sm">Created:</span>
                      <span className="font-medium">
                        {formatDate(selectedBatch.createdAt)}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="bg-gray-100 rounded-lg p-3">
                  <h3 className="font-semibold text-gray-900 text-sm mb-2 flex items-center gap-1">
                    <Mail size={14} />
                    Email Settings
                  </h3>
                  <div className="space-y-1 text-sm">
                    <div className="flex justify-between items-center">
                      <span className="text-grey">Reply To:</span>
                      <div className="flex items-center gap-1">
                        <span className="font-medium text-xs">
                          {selectedBatch.replyTo}
                        </span>
                      </div>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-grey">Tracking IDs:</span>
                      <span className="font-medium">
                        {selectedBatch.responses
                          ? selectedBatch.responses.length
                          : 0}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Recipients List */}
              <div className="mb-4">
                <h3 className="font-semibold text-gray-900 text-sm mb-2 flex items-center gap-1">
                  <Users size={14} />
                  Recipients ({getTotalRecipients(selectedBatch)})
                </h3>
                <div className="bg-gray-50 rounded-lg p-3 max-h-36 overflow-y-auto">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-1">
                    {selectedBatch.recipients.map((email, index) => (
                      <div
                        key={index}
                        className="flex items-center gap-1 text-xs"
                      >
                        <Mail size={12} className="text-gray-400" />
                        <span className="truncate">{email}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Response Details */}
              {selectedBatch.responses &&
                selectedBatch.responses.length > 0 && (
                  <div>
                    <h3 className="font-semibold text-gray-900 text-sm mb-2 flex items-center gap-1">
                      <Globe size={14} />
                      Response Details
                    </h3>
                    <div className="space-y-2">
                      {selectedBatch.responses.map((response, index) => {
                        const parsed = parseResponse(response);
                        return (
                          <div
                            key={index}
                            className="bg-gray-50 rounded-lg p-3"
                          >
                            <div className="flex items-center justify-between mb-1">
                              <span className="font-medium text-xs">
                                Batch {index + 1}
                              </span>
                              {parsed.trackId && (
                                <div className="flex items-center gap-1">
                                  <span className="text-xs text-gray-500">
                                    Track ID:
                                  </span>
                                  <div className="flex items-center gap-1">
                                    <code className="bg-gray-200 px-1 py-0.5 rounded text-xs font-mono">
                                      {parsed.trackId}
                                    </code>
                                  </div>
                                </div>
                              )}
                            </div>
                            <div className="text-xs text-gray-600">
                              <div className="flex items-center gap-1 mb-1">
                                <CheckCircle
                                  size={12}
                                  className="text-green-600"
                                />
                                <span>
                                  {parsed.emailCount} emails sent successfully
                                </span>
                              </div>
                              <details className="mt-1">
                                <summary className="cursor-pointer text-blue-600 hover:text-blue-800 text-xs">
                                  View email IDs
                                </summary>
                                <div className="mt-1 p-1 bg-white rounded border max-h-24 overflow-y-auto">
                                  {parsed.emails.map((email, emailIndex) => (
                                    <div
                                      key={emailIndex}
                                      className="text-xs font-mono text-gray-600 mb-0.5"
                                    >
                                      {email}
                                    </div>
                                  ))}
                                </div>
                              </details>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};
